import { useMemo, useRef, type FocusEvent, type ReactNode } from 'react'
import { Input } from '../ui/input'
import { Popover, PopoverAnchor, PopoverContent } from '../ui/popover'
import { useIsReadOnly } from '@/lib/casl'
import { Typography } from '@/components/ui/typography'
import { ComboboxListbox, useComboboxListbox } from './combobox'

export type AutoCompleteItem = {
  value: string
  label: string
}

export type AutoCompleteProps = {
  id?: string
  ariaLabelledBy?: string
  selectedValue: string
  onSelectedValueChange: (value: string) => void
  searchValue: string
  onSearchValueChange: (value: string) => void
  items: readonly AutoCompleteItem[]
  isLoading?: boolean
  emptyMessage?: ReactNode
  placeholder?: string
  readOnlyEmptyText?: ReactNode
  disabled?: boolean
}

// https://leonardomontini.dev/shadcn-autocomplete/
function AutoComplete({
  id,
  ariaLabelledBy,
  selectedValue,
  onSelectedValueChange,
  searchValue,
  onSearchValueChange,
  items,
  isLoading,
  emptyMessage = 'No items.',
  placeholder = 'Search',
  readOnlyEmptyText = 'No selection',
  disabled = false,
}: AutoCompleteProps) {
  const inputRef = useRef<HTMLInputElement>(null)
  const contentRef = useRef<HTMLDivElement>(null)
  const { isReadOnly } = useIsReadOnly()
  const {
    activateOption,
    activeIndex,
    activeOptionId,
    close,
    handleKeyDown,
    isOpen,
    listboxId,
    openWithFirstOption,
  } = useComboboxListbox({
    id: id ? `${id}-options` : undefined,
    optionCount: items.length,
  })

  const labels = useMemo(
    () =>
      items.reduce<Record<string, string>>((acc, item) => {
        acc[item.value] = item.label
        return acc
      }, {}),
    [items]
  )

  const clearSelection = () => {
    onSelectedValueChange('')
    onSearchValueChange('')
  }

  const resetSearchToSelection = () => {
    if (selectedValue) {
      onSearchValueChange(labels[selectedValue] ?? '')
      return
    }

    onSearchValueChange('')
  }

  const handleSearchValueChange = (value: string) => {
    openWithFirstOption()
    onSearchValueChange(value)
  }

  const onInputBlur = (e: FocusEvent<HTMLInputElement>) => {
    if (e.relatedTarget instanceof Node && contentRef.current?.contains(e.relatedTarget)) {
      return
    }

    close()

    if (labels[selectedValue] !== searchValue) {
      resetSearchToSelection()
    }
  }

  const onSelectItem = (inputValue: string) => {
    if (inputValue === selectedValue) {
      clearSelection()
    } else {
      onSelectedValueChange(inputValue)
      onSearchValueChange(labels[inputValue] ?? '')
    }
    close()
  }

  if (isReadOnly) {
    const displayValue = selectedValue ? labels[selectedValue] || selectedValue : readOnlyEmptyText
    return (
      <div className="flex items-center">
        <Typography variant="label" tone={!selectedValue ? 'muted' : 'default'}>
          {displayValue}
        </Typography>
      </div>
    )
  }

  return (
    <div className="flex w-full items-center">
      <Popover
        open={isOpen}
        onOpenChange={(nextOpen) => (nextOpen ? openWithFirstOption() : close())}
      >
        <PopoverAnchor asChild>
          <Input
            ref={inputRef}
            id={id}
            aria-labelledby={ariaLabelledBy}
            aria-controls={isOpen ? listboxId : undefined}
            aria-expanded={isOpen}
            aria-autocomplete="list"
            aria-activedescendant={activeOptionId}
            role="combobox"
            placeholder={placeholder}
            className="text-left"
            value={searchValue}
            disabled={disabled}
            onChange={(e) => handleSearchValueChange(e.target.value)}
            onKeyDown={(event) =>
              handleKeyDown(event, (index) => {
                // The hook clamps the active index to `items.length`, so this lookup always hits.
                const item = items[index]
                if (item) onSelectItem(item.value)
              })
            }
            onMouseDown={() => {
              if (!disabled) {
                openWithFirstOption()
              }
            }}
            onFocus={() => {
              if (!disabled) openWithFirstOption()
            }}
            onBlur={onInputBlur}
          />
        </PopoverAnchor>
        <PopoverContent
          ref={contentRef}
          id={listboxId}
          role="listbox"
          onOpenAutoFocus={(e) => e.preventDefault()}
          onInteractOutside={(e) => {
            if (e.target === inputRef.current) {
              e.preventDefault()
            }
          }}
          align="start"
          size="trigger"
          padding="none"
        >
          <ComboboxListbox
            items={items}
            listboxId={listboxId}
            activeIndex={activeIndex}
            selectedOptionKey={selectedValue}
            showSelectionIndicator
            isLoading={isLoading}
            emptyMessage={emptyMessage ?? 'No items.'}
            getOptionKey={(option) => option.value}
            getOptionLabel={(option) => option.label}
            onActivateOption={activateOption}
            onSelectOption={(option) => onSelectItem(option.value)}
          />
        </PopoverContent>
      </Popover>
    </div>
  )
}

export default AutoComplete
